import { AuditEntry, CapId, NAV, StaffMemberView, StaffRole, StaffState } from '../types';

// Pure rules of the staff views. The server computes effectiveCaps itself and is the authority;
// the panel recomputes the same thing locally to show a change at once and to draw the matrix of
// what is configured (the server's list is empty for a suspended or expired person).

// A sober palette for the roles (readable on the ink background)
export const ROLE_COLORS = ['#c9a24f', '#7aa2b8', '#8fbf8a', '#d08a7a', '#a78bba', '#6fb3a8', '#b9b3a4', '#c7c06a'];

export const COLOR_RE = /^#[0-9a-fA-F]{6}$/;

export const DISCORD_ID_RE = /^\d{5,25}$/;

export type MemberStatus = 'active' | 'suspended' | 'expired';

export const statusOf = (m: StaffMemberView): MemberStatus => (!m.active ? 'suspended' : m.expired ? 'expired' : 'active');

export const STATUS_LABEL: Record<MemberStatus, string> = { active: 'Actif', suspended: 'Suspendu', expired: 'Expiré' };

// Matrix order: the order of the server's capInfo, minus the cap nobody can be given
export const grantable = (staff: StaffState): Array<{ id: CapId; label: string; description: string }> => staff.capInfo.filter((c) => c.id !== 'staff');

export const roleOf = (staff: StaffState, id: string | null): StaffRole | null => (id ? staff.roles.find((r) => r.id === id) || null : null);

// (role caps + added) - denied, in matrix order
export const finalCaps = (order: CapId[], role: StaffRole | null, caps: CapId[], denied: CapId[]): CapId[] =>
  order.filter((c) => ((role && role.caps.indexOf(c) !== -1) || caps.indexOf(c) !== -1) && denied.indexOf(c) === -1);

export type CapState = 'inherit' | 'add' | 'deny';

// What a menu is for this person, compared to the role: nothing special, added on top, or taken away
export const capStateOf = (role: StaffRole | null, caps: CapId[], denied: CapId[], cap: CapId): CapState =>
  denied.indexOf(cap) !== -1 ? 'deny' : caps.indexOf(cap) !== -1 && !(role && role.caps.indexOf(cap) !== -1) ? 'add' : 'inherit';

// Sets one menu to a state, keeping the two lists consistent with the role
export const withCapState = (role: StaffRole | null, caps: CapId[], denied: CapId[], cap: CapId, state: CapState): { caps: CapId[]; denied: CapId[] } => {
  const inRole = !!role && role.caps.indexOf(cap) !== -1;
  const c = caps.filter((x) => x !== cap);
  const d = denied.filter((x) => x !== cap);
  if (state === 'add' && !inRole) c.push(cap);
  if (state === 'deny' && inRole) d.push(cap);
  return { caps: c, denied: d };
};

// After a change of role: drop what the new role already gives (added) or never had (denied)
export const normalizeOverrides = (role: StaffRole | null, caps: CapId[], denied: CapId[]): { caps: CapId[]; denied: CapId[] } => ({
  caps: caps.filter((c) => !(role && role.caps.indexOf(c) !== -1)),
  denied: denied.filter((c) => !!role && role.caps.indexOf(c) !== -1),
});

// The final state of one menu, as the matrix draws it
export const cellState = (role: StaffRole | null, caps: CapId[], denied: CapId[], cap: CapId): 'inherit-on' | 'add' | 'deny' | 'off' => {
  const s = capStateOf(role, caps, denied, cap);
  if (s === 'deny') return 'deny';
  if (s === 'add') return 'add';
  return role && role.caps.indexOf(cap) !== -1 ? 'inherit-on' : 'off';
};

// What the person will find in the panel with these caps: the menus, then the actions inside them
export const previewMenus = (caps: CapId[]): { menus: string[]; actions: string[] } => {
  const menus = NAV.filter((n) => !n.cap || (n.cap !== 'staff' && caps.indexOf(n.cap) !== -1)).map((n) => n.label);
  const actions: string[] = [];
  if (caps.indexOf('kick') !== -1) actions.push('Expulser');
  if (caps.indexOf('ban') !== -1) actions.push('Bannir et débannir');
  if (caps.indexOf('items') !== -1) actions.push('Objets, or et inventaires');
  if (caps.indexOf('restart') !== -1) actions.push('Redémarrage');
  if (caps.indexOf('console') !== -1) actions.push('Commandes en jeu');
  return { menus, actions };
};

export const sameSet = (a: CapId[], b: CapId[]): boolean => a.length === b.length && a.every((c) => b.indexOf(c) !== -1);

export const DAY = 86400000;

// "expire dans 5 jours" / "expiré depuis 2 jours" / "" for a permanent access
export const describeExpiry = (m: Pick<StaffMemberView, 'expiresAt' | 'expired'>, now: number): string => {
  if (!m.expiresAt) return '';
  const diff = m.expiresAt - now;
  const abs = Math.abs(diff);
  const days = Math.floor(abs / DAY);
  const hours = Math.floor(abs / 3600000);
  const span = days >= 2 ? `${days} jours` : hours >= 1 ? `${hours} h` : 'moins d\'une heure';
  return diff > 0 && !m.expired ? `expire dans ${span}` : `expiré depuis ${span}`;
};

// A calendar date for <input type="date"> ("2026-11-04"), in local time
export const toDateInput = (ms: number | null): string => {
  if (!ms) return '';
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

// End of that day, local time (an access "until the 4th" lasts through the 4th)
export const fromDateInput = (v: string): number | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 23, 59, 0).getTime();
};

export const AUDIT_LABEL: Record<string, string> = {
  'member.add': 'Membre ajouté',
  'member.update': 'Membre modifié',
  'member.remove': 'Membre retiré',
  'member.suspend': 'Membre suspendu',
  'member.activate': 'Membre réactivé',
  'member.expire': 'Accès expiré',
  'role.add': 'Rôle créé',
  'role.update': 'Rôle modifié',
  'role.remove': 'Rôle supprimé',
  bulk: 'Action groupée',
};

export type AuditGroup = 'all' | 'member' | 'role' | 'bulk';

export const auditGroupOf = (e: AuditEntry): AuditGroup => (e.action === 'bulk' ? 'bulk' : e.action.indexOf('role.') === 0 ? 'role' : 'member');

// "Prolonger de N jours" applies from the current end date when it is still ahead, else from now
export const extendedExpiry = (m: StaffMemberView, days: number, now: number): number => Math.max(now, m.expiresAt || 0) + days * DAY;

export const stripMember = (m: StaffMemberView): Record<string, unknown> => ({
  discordId: m.discordId,
  name: m.name,
  roleId: m.roleId,
  caps: m.caps,
  denied: m.denied,
  note: m.note,
  active: m.active,
  expiresAt: m.expiresAt,
});

export const stripRole = (r: StaffRole): Record<string, unknown> => ({ id: r.id, name: r.name, description: r.description, color: r.color, caps: r.caps });
